import { Card, Skeleton, Stack } from "@mui/material";
import { ContainerGrid, MetricsGridItem } from "src/components";
import { MetricsFilterByOption } from "src/constants";
import { MetricDto, useGetMetricCollection, useRemoveMetricFromCollection } from "src/http";

interface IProps {
  metricCollectionGuid: string;
  userCanRemove?: (metric: MetricDto) => boolean;
  filters: Record<MetricsFilterByOption, string[]>;
}

const MetricsGrid = (props: IProps) => {
  const { data: metrics, isLoading } = useGetMetricCollection(props.metricCollectionGuid);
  const { mutate: removeMetric } = useRemoveMetricFromCollection();

  function filterFn(metric: MetricDto) {
    let passesTeamFilter = true;
    let passesReportingUserFilter = true;
    let passesTagFilter = true;

    if (props.filters[MetricsFilterByOption.Team].length > 0) {
      passesTeamFilter = props.filters[MetricsFilterByOption.Team].includes(metric.assignedTeam.name);
    }

    if (props.filters[MetricsFilterByOption.ReportingPerson].length > 0) {
      passesReportingUserFilter = props.filters[MetricsFilterByOption.ReportingPerson].includes(
        metric.reportingUser?.displayName ?? "None",
      );
    }

    if (props.filters[MetricsFilterByOption.Tag].length > 0) {
      passesTagFilter = metric.tags.some((x) => props.filters[MetricsFilterByOption.Tag].includes(x));
    }

    return passesTeamFilter && passesReportingUserFilter && passesTagFilter;
  }

  function handleMetricRemoved(metricGuid: string) {
    removeMetric({
      metricCollectionGuid: props.metricCollectionGuid,
      metricGuid: metricGuid,
    });
  }

  return (
    <>
      {isLoading && (
        <ContainerGrid container spacing={1}>
          {[...Array(3)].map((_, i) => (
            <ContainerGrid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
              <Card sx={{ p: 1 }}>
                <Stack spacing={1}>
                  <Skeleton variant="rectangular" />
                  <Skeleton variant="rectangular" height="250px" />
                </Stack>
              </Card>
            </ContainerGrid>
          ))}
        </ContainerGrid>
      )}

      {!isLoading && metrics && (
        <ContainerGrid container spacing={1}>
          {metrics
            .filter(filterFn)
            .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
            .map((metric, index) => (
              <ContainerGrid key={metric.guid} size={{ xs: 12, md: 6, lg: 4 }} sx={{ height: "400px" }}>
                <MetricsGridItem
                  key={index}
                  metric={metric}
                  metricIndex={index}
                  userCanRemove={props.userCanRemove}
                  onMetricRemoved={handleMetricRemoved}
                />
              </ContainerGrid>
            ))}
        </ContainerGrid>
      )}
    </>
  );
};

export { MetricsGrid };
